import { Check, ChevronDown, Radio } from "lucide-react";

import type { Provider, CatalogServiceInfo } from "@/api/types";
import { ProviderIcon } from "@/components/ProviderIcon";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui";
import { cn } from "@/lib/cn";

export type ProviderFilterValue = Provider | "all";

/** A provider we can actually send a query to. */
export function isSearchable(info: CatalogServiceInfo): boolean {
  return (
    Boolean(info.can_search) &&
    info.health !== "unavailable" &&
    info.health !== "disabled"
  );
}

/** Capabilities come from catalog services, never audio providers. */
export function supportsAlbums(info: CatalogServiceInfo): boolean {
  return isSearchable(info) && Boolean(info.can_search_albums);
}

/** One clause explaining where albums come from, for a disabled Albums tab. */
export function albumSourceNote(albumProviders: CatalogServiceInfo[]): string {
  const names = albumProviders.map((info) => info.name);
  if (names.length === 0) return "No music source on this server offers albums.";
  if (names.length === 1) return `${names[0]} is the only source with albums.`;
  const last = names[names.length - 1];
  return `Only ${names.slice(0, -1).join(", ")} and ${last} have albums.`;
}

export function providerLabel(providers: ReadonlyArray<{ slug: string; name: string }>, slug: string): string {
  const known = providers.find((info) => info.slug === slug);
  if (known) return known.name;
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

interface ChipProps {
  label: string;
  selected: boolean;
  disabled?: boolean;
  /** Why the chip is unavailable. Announced and shown on hover. */
  reason?: string;
  onClick: () => void;
}

function Chip({
  label,
  selected,
  disabled = false,
  reason,
  onClick,
}: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={reason ? `${label} — ${reason}` : undefined}
      title={reason}
      className={cn(
        "h-11 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors",
        selected
          ? "border-accent bg-accent text-on-accent"
          : "border-border bg-surface-raised text-text hover:bg-surface-hover",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface-raised",
      )}
    >
      {label}
    </button>
  );
}

export interface ProviderFilterProps {
  providers: CatalogServiceInfo[];
  value: ProviderFilterValue;
  onChange: (value: ProviderFilterValue) => void;
  className?: string;
}

export function ProviderFilter({
  providers,
  value,
  onChange,
  className,
}: ProviderFilterProps) {
  return (
    <div
      role="group"
      aria-label="Limit results to one music source"
      className={cn("flex gap-2 overflow-x-auto scrollbar-none", className)}
    >
      <Chip
        label="All"
        selected={value === "all"}
        onClick={() => onChange("all")}
      />

      {providers.map((info) => {
        const usable = isSearchable(info);
        return (
          <Chip
            key={info.slug}
            label={info.name}
            selected={value === info.slug}
            disabled={!usable}
            reason={
              usable
                ? undefined
                : info.message || "not configured on this server"
            }
            onClick={() => onChange(info.slug)}
          />
        );
      })}
    </div>
  );
}

export interface ProviderMenuProps {
  providers: CatalogServiceInfo[];
  value: ProviderFilterValue;
  onChange: (value: ProviderFilterValue) => void;
  className?: string;
}

/**
 * The source filter, as one control rather than a row of chips.
 *
 * Chips read well but cost a whole line of a sticky toolbar, and on a phone
 * that line is permanently subtracted from the results underneath. A menu says
 * the same thing in one button's width and still shows every provider —
 * including the ones that cannot be used, which stay listed and disabled with
 * the reason attached, because a source silently missing from the list is how
 * someone concludes the app is broken.
 */
export function ProviderMenu({
  providers,
  value,
  onChange,
  className,
}: ProviderMenuProps) {
  const label =
    value === "all" ? "All sources" : providerLabel(providers, value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className={cn(
            "h-8 shrink-0 gap-1 rounded-full px-2 text-xs text-text-muted before:inset-x-0 hover:text-text",
            value !== "all" && "border-accent/30 bg-accent-soft text-accent",
            className,
          )}
          aria-label={`Search source: ${label}`}
          title={`Search source: ${label}`}
        >
          {value === "all" ? (
            <Radio className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : <ProviderIcon provider={value} className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{label}</span>
          <ChevronDown
            className="h-3 w-3 shrink-0"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Search source</DropdownMenuLabel>

        <DropdownMenuItem
          onSelect={() => onChange("all")}
          trailing={
            value === "all" ? (
              <Check className="h-4 w-4 text-accent" aria-hidden="true" />
            ) : null
          }
        >
          All sources
        </DropdownMenuItem>

        {providers.map((info) => {
          const usable = isSearchable(info);
          return (
            <DropdownMenuItem
              key={info.slug}
              disabled={!usable}
              onSelect={() => onChange(info.slug)}
              trailing={
                value === info.slug ? (
                  <Check className="h-4 w-4 text-accent" aria-hidden="true" />
                ) : null
              }
            >
              <span className="flex items-center gap-3">
                <ProviderIcon provider={info.slug} />
                <span className="truncate">
                  {usable
                    ? info.name
                    : `${info.name} — ${info.message || "not configured"}`}
                </span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
